import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFooter,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CRow,
  CTextarea,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useGetApiKey, useResetApiKey } from "./UserService";
import {
  useGetSettingsDefinitions,
  useGetUserSettings,
  useUpdateUserSettings,
} from "./SettingsService";
import {
  useGetUserProfile,
  useResetOnboarding,
  useUpdateUserProfile,
} from "./ProfileService";
import { useHistory } from "react-router-dom";

import DynamicSettingsForm from "./components/DynamicSettingsForm"; // Import DynamicSettingsForm
import MyGroupsCard from "./components/MyGroupsCard";
import { NucToast } from "src/reusable/components/Toast";
import PropTypes from "prop-types";
// Import settings hooks
import { useForm } from "react-hook-form"; // Import useForm
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useOnboardingStore } from "src/views/assistant/stores/OnboardingStore";

function _toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export const UserAPIKeyBadge = ({ getVisibility }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const { data: apiKeyData } = useGetApiKey(showApiKey);

  return (
    <>
      {showApiKey ? (
        <div
          id="api-key-div"
          data-testid="uprof-text-api-key"
          style={{
            wordBreak: "break-all",
          }}
        >
          {apiKeyData?.access_token}{" "}
        </div>
      ) : (
        <CBadge
          id="show-api-key-badge"
          data-testid="uprof-badge-reveal-api-key"
          color="success"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setShowApiKey(true);
            getVisibility(true);
          }}
        >
          Click to reveal API Key
        </CBadge>
      )}
    </>
  );
};

UserAPIKeyBadge.propTypes = { getVisibility: PropTypes.func };

const UserAPIKey = ({ className }) => {
  const [confirmApiKeyResetVisible, setConfirmApiKeyResetVisible] =
    useState(false);

  const [showApiKey, setShowApiKey] = useState(false);
  const getVisibility = (visibility) => {
    setShowApiKey(visibility);
  };

  const handleClick = () => {
    const text = document.getElementById("api-key-div")?.innerText;

    if (!text) {
      return;
    }

    // Check if Clipboard API is supported
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          NucToast({
            message: "API Key copied to clipboard",
            toastId: "api-key-copied-toast",
            className: "api-key-copied-toast",
            id: "api-key-copied-toast",
            type: "success",
          });
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          // Use fallback method
          NucToast({
            message: "Cannot copy API Key. Please select and copy manually.",
            type: "warning",
            toastId: "api-key-copy-warning-toast",
          });
        });
    }
  };

  const { mutate: resetApiKey } = useResetApiKey();

  return (
    <CCard className={className} data-testid="uprof-card-api-key">
      <CCardHeader>
        <h4 data-testid="uprof-heading-api-key">API Key</h4>
      </CCardHeader>
      <CCardBody>{<UserAPIKeyBadge getVisibility={getVisibility} />}</CCardBody>
      <CFooter>
        <CButton
          data-testid="uprof-btn-reset-api-key"
          data-attr="users:btn:reset-api-key"
          color="warning"
          onClick={() => setConfirmApiKeyResetVisible(true)}
          size="sm"
          id="reset-api-key-button"
        >
          Reset API Key
        </CButton>
        {showApiKey === true && (
          <CButton
            data-testid="uprof-btn-copy-api-key"
            data-attr="users:btn:copy-api-key"
            hidden={!navigator.clipboard}
            color="info"
            onClick={() => {
              handleClick();
            }}
            size="sm"
            id="copy-api-key-button"
            style={{ marginLeft: "auto", marginRight: "0" }}
          >
            Copy API Key
          </CButton>
        )}

        <CModal
          data-testid="uprof-dialog-reset-api-key"
          show={confirmApiKeyResetVisible}
          onClose={() => setConfirmApiKeyResetVisible(false)}
        >
          <CModalHeader>
            <h4 data-testid="uprof-dialog-reset-title">Reset API Key</h4>
          </CModalHeader>
          <CModalBody data-testid="uprof-dialog-reset-body">
            Are you sure you want to reset your API Key? Any processes using the
            existing API Key will no longer work.
          </CModalBody>
          <CModalFooter>
            <CButton
              data-testid="uprof-btn-confirm-reset-api-key"
              data-attr="users:btn:confirm-reset-api-key"
              color="danger"
              onClick={() => {
                resetApiKey();
                setConfirmApiKeyResetVisible(false);
              }}
              id="confirm-reset-api-key-button"
            >
              Yes, Reset the API Key
            </CButton>
          </CModalFooter>
        </CModal>
      </CFooter>
    </CCard>
  );
};

const UserProfile = () => {
  const user = useGetUserDetails();
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useGetUserProfile();

  if (!user) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Loading user profile...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }} data-testid="uprof-page">
      <CRow className="mb-4">
        <CCol>
          <h2 data-testid="uprof-title">User Profile</h2>
        </CCol>
      </CRow>

      <CRow className="mb-4">
        <CCol md="6" className="d-flex flex-column mb-3 mb-md-0">
          <UserProfileDetails user={user} className="flex-grow-1" />
        </CCol>
        <CCol md="6" className="d-flex flex-column">
          <UserAPIKey className="flex-grow-1" />
        </CCol>
      </CRow>

      <CRow>
        <CCol md="12">
          <ProfessionalProfile
            user={user}
            profileData={profileData}
            profileLoading={profileLoading}
            profileError={profileError}
          />
        </CCol>
      </CRow>

      <CRow className="mb-4">
        <CCol md="12">
          <MyGroupsCard />
        </CCol>
      </CRow>

      <CRow>
        <CCol md="12">
          <UserSettingsSection />
        </CCol>
      </CRow>

      <DevTools />
    </div>
  );
};

/**
 * Dev-only debug tools. Rendered only in local dev (Vite DEV mode) so the
 * button is invisible in customer deployments; the backend endpoint is still
 * self-scoped so it stays safe if someone does hit it directly.
 */
const DevTools = () => {
  const history = useHistory();
  const { mutate: resetOnboarding, isLoading } = useResetOnboarding();
  const resetOnboardingStore = useOnboardingStore((s) => s.resetOnboarding);

  if (!import.meta.env.DEV) return null;

  const handleReset = () => {
    resetOnboarding(undefined, {
      onSuccess: () => {
        // Wipe the local store so the verification pane and profile
        // artifact from the previous run don't bleed into the new session.
        resetOnboardingStore();
        NucToast({
          message:
            "FTUX onboarding reset — redirecting to /assistant to retrigger.",
          type: "success",
          hideProgressBar: false,
        });
        history.push("/assistant");
      },
      onError: () => {
        NucToast({
          message: "Failed to reset onboarding.",
          type: "error",
          hideProgressBar: false,
        });
      },
    });
  };

  return (
    <CRow className="mt-4">
      <CCol md="12">
        <CCard>
          <CCardHeader>
            <h4>Dev Tools</h4>
            <small className="text-muted">
              Only visible in local development.
            </small>
          </CCardHeader>
          <CCardBody>
            <CButton
              color="warning"
              onClick={handleReset}
              disabled={isLoading}
              data-testid="uprof-dev-reset-ftux"
            >
              {isLoading ? "Resetting…" : "Reset FTUX Onboarding"}
            </CButton>
            <div className="mt-2">
              <small className="text-muted">
                Flips <code>onboarding_completed</code> off for your user and
                navigates to the assistant so the FTUX flow retriggers.
              </small>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

const UserSettingsSection = () => {
  const {
    data: definitions,
    isLoading: definitionsLoading,
    error: definitionsError,
  } = useGetSettingsDefinitions();

  const {
    data: settings, // This is currentSettings
    isLoading: settingsLoading,
    error: settingsError,
  } = useGetUserSettings();

  const { mutate: updateSettings, isLoading: isUpdating } =
    useUpdateUserSettings();

  const formMethods = useForm({
    defaultValues: settings || {}, // Initialize with current settings
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    if (settings) {
      reset(settings); // Reset form when settings data is loaded/changed
    }
  }, [settings, reset]);

  const actualSubmitHandler = (formData) => {
    // Renamed from handleSettingsSubmit to avoid confusion with RHF handleSubmit
    updateSettings(formData, {
      onSuccess: () => {
        NucToast({
          message: "Settings updated successfully!",
          type: "success",
        });
      },
      onError: (error) => {
        NucToast({
          message: "Failed to update settings. Please try again.",
          type: "error",
        });
        console.error("Error updating settings:", error);
      },
    });
  };

  if (definitionsLoading || settingsLoading) {
    return (
      <CCard>
        <CCardHeader>
          <h4>User Settings</h4>
        </CCardHeader>
        <CCardBody>
          <p>Loading settings...</p>
        </CCardBody>
      </CCard>
    );
  }

  if (definitionsError || settingsError) {
    return (
      <CCard>
        <CCardHeader>
          <h4>User Settings - Error</h4>
        </CCardHeader>
        <CCardBody>
          <p>
            There was an error loading your settings. Please check the console
            for more details.
          </p>
          {definitionsError && (
            <p>Definitions Error: {String(definitionsError)}</p>
          )}
          {settingsError && <p>Settings Error: {String(settingsError)}</p>}
        </CCardBody>
      </CCard>
    );
  }

  if (!definitions || definitions.length === 0) {
    return (
      <CCard>
        <CCardHeader>
          <h4>User Settings</h4>
        </CCardHeader>
        <CCardBody>
          <p className="text-info">
            No setting definitions found. The backend may not have any settings
            configured yet.
          </p>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard data-testid="uprof-card-settings">
      <CCardHeader>
        <h4 data-testid="uprof-heading-settings">User Settings</h4>
      </CCardHeader>
      <CForm onSubmit={handleSubmit(actualSubmitHandler)}>
        <CCardBody>
          <DynamicSettingsForm
            definitions={definitions}
            formMethods={formMethods}
            // currentSettings, onSubmit, isLoading are no longer direct props
          />
        </CCardBody>
        <CFooter>
          <CButton
            data-testid="uprof-btn-save-settings"
            data-attr="users:btn:save-user-settings"
            type="submit"
            color="primary"
            disabled={isUpdating}
            className="mr-2"
          >
            {isUpdating ? "Saving..." : "Save Settings"}
          </CButton>
          <CButton
            data-testid="uprof-btn-reset"
            data-attr="users:btn:reset-settings"
            type="button"
            color="secondary"
            onClick={() => reset(settings || {})}
            disabled={isUpdating}
          >
            Reset
          </CButton>
        </CFooter>
      </CForm>
    </CCard>
  );
};

const ProfessionalProfile = ({ profileData, profileLoading, profileError }) => {
  const [jobTitle, setJobTitle] = useState("");
  const [industry, setIndustry] = useState("");
  const [skills, setSkills] = useState("");
  const [summary, setSummary] = useState("");
  const [goals, setGoals] = useState("");

  const { mutate: updateProfile, isLoading: isUpdating } =
    useUpdateUserProfile();

  // Initialize form fields when profile data is loaded
  useEffect(() => {
    if (profileData) {
      setJobTitle(profileData.job_title || "");
      setIndustry(profileData.industry || "");
      setSkills(profileData.key_skills || "");
      setSummary(profileData.professional_summary || "");
      setGoals(profileData.primary_goals_for_atomassist || "");
    }
  }, [profileData]);

  const handleSave = () => {
    const profileUpdateData = {
      job_title: jobTitle,
      industry: industry,
      key_skills: skills,
      professional_summary: summary,
      primary_goals_for_atomassist: goals,
    };

    updateProfile(profileUpdateData, {
      onSuccess: () => {
        NucToast({
          message: "Profile information saved successfully!",
          toastId: "profile-saved-toast",
          type: "success",
        });
      },
      onError: (error) => {
        NucToast({
          message: "Failed to save profile information. Please try again.",
          toastId: "profile-error-toast",
          type: "error",
        });
        console.error("Error saving profile:", error);
      },
    });
  };

  if (profileError) {
    return (
      <CCard>
        <CCardHeader>
          <h4>Professional Profile</h4>
        </CCardHeader>
        <CCardBody>
          <p>Error loading profile data. Please refresh the page.</p>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard data-testid="uprof-card-professional">
      <CCardHeader>
        <h4 data-testid="uprof-heading-professional">Professional Profile</h4>
      </CCardHeader>
      <CCardBody>
        {profileLoading ? (
          <p>Loading profile data...</p>
        ) : (
          <CForm>
            <CFormGroup>
              <CLabel htmlFor="jobTitle">Job Title / Primary Role</CLabel>
              <CInput
                data-testid="uprof-input-job-title"
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g., Senior Software Engineer"
              />
            </CFormGroup>
            <CFormGroup>
              <CLabel htmlFor="industry">Industry</CLabel>
              <CInput
                data-testid="uprof-input-industry"
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., Technology - Software"
              />
            </CFormGroup>
            <CFormGroup>
              <CLabel htmlFor="skills">Key Skills / Areas of Expertise</CLabel>
              <CTextarea
                data-testid="uprof-input-expertise"
                id="skills"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="e.g., Python, Machine Learning, Data Analysis"
              />
            </CFormGroup>
            <CFormGroup>
              <CLabel htmlFor="summary">Brief Professional Summary</CLabel>
              <CTextarea
                data-testid="uprof-input-bio"
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows="3"
                placeholder="Describe your main responsibilities or professional interests."
              />
            </CFormGroup>
            <CFormGroup>
              <CLabel htmlFor="goals">
                Primary Goals for using AtomAssist
              </CLabel>
              <CTextarea
                data-testid="uprof-input-interests"
                id="goals"
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                placeholder="e.g., Drafting technical documents, Brainstorming marketing campaigns"
              />
            </CFormGroup>
          </CForm>
        )}
      </CCardBody>
      <CFooter>
        <CButton
          data-testid="uprof-btn-save-profile"
          data-attr="users:btn:save-professional-profile"
          color="primary"
          onClick={handleSave}
          disabled={isUpdating || profileLoading}
        >
          {isUpdating ? "Saving..." : "Save Profile"}
        </CButton>
      </CFooter>
    </CCard>
  );
};

ProfessionalProfile.propTypes = {
  user: PropTypes.object.isRequired,
  profileData: PropTypes.object,
  profileLoading: PropTypes.bool,
  profileError: PropTypes.object,
};

const UserProfileDetails = ({ user, className }) => {
  if (!user) return null;

  return (
    <CCard className={className} data-testid="uprof-card-account">
      <CCardHeader>
        <h4 data-testid="uprof-heading-email">
          {user.user_display_name || "User Details"}
        </h4>
      </CCardHeader>
      <CCardBody>
        <CFormGroup row className="my-2">
          <CCol md="5">
            <strong>Roles:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-roles">
            {user.roles?.map((role) => _toTitleCase(role.name)).join(", ") ||
              "N/A"}
          </CCol>
        </CFormGroup>
        <CFormGroup row className="my-2">
          <CCol md="5">
            <strong>User Unique ID:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-unique-id">
            {user.username || "N/A"}
          </CCol>
        </CFormGroup>
        <CFormGroup row className="my-2">
          <CCol md="5">
            <strong>User ID:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-user-id">
            {user.id || "N/A"}
          </CCol>
        </CFormGroup>
        <CFormGroup row className="my-2">
          <CCol md="5">
            <strong>Account Creation Date:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-creation-date">
            {user.create_date
              ? new Date(user.create_date).toLocaleDateString()
              : "N/A"}
          </CCol>
        </CFormGroup>
        <CFormGroup row className="my-2">
          <CCol md="5">
            <strong>Email Validated:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-email-validated">
            {user.email_validated ? "Yes" : "No"}
          </CCol>
        </CFormGroup>
        <CFormGroup row className="my-2 mb-0">
          <CCol md="5">
            <strong>Account Status:</strong>
          </CCol>
          <CCol md="7" data-testid="uprof-text-account-status">
            {user.active ? "Active" : "Inactive"}
          </CCol>
        </CFormGroup>
      </CCardBody>
    </CCard>
  );
};

UserProfileDetails.propTypes = {
  user: PropTypes.object.isRequired,
  className: PropTypes.string,
};

UserAPIKey.propTypes = {
  className: PropTypes.string,
};

export default UserProfile;
